import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * MarkdownViewer - Reusable component for rendering Markdown content
 * Supports GitHub Flavored Markdown (tables, strikethrough, task lists, etc.)
 */
function MarkdownViewer({ content, className = '' }) {
  if (!content) {
    return null;
  }

  return (
    <div className={`prose prose-sm dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom table styling for better readability
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border-collapse border border-border-strong" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-surface-muted" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th className="border border-border-strong px-3 py-2 text-left text-sm font-semibold" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-border-strong px-3 py-2 text-sm" {...props} />
          ),
          // Code blocks
          code: ({ node, inline, className, children, ...props }) => {
            if (inline) {
              return (
                <code className="bg-surface-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <pre className="bg-surface-muted p-4 rounded-lg overflow-x-auto">
                <code className="text-sm font-mono" {...props}>
                  {children}
                </code>
              </pre>
            );
          },
          // Blockquotes
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-primary-500 pl-4 italic my-4 text-content-secondary" {...props} />
          ),
          // Horizontal rules
          hr: ({ node, ...props }) => (
            <hr className="my-6 border-border-strong" {...props} />
          ),
          // Links
          a: ({ node, ...props }) => (
            <a className="text-accent hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
          ),
          // Headers with better spacing
          h1: ({ node, ...props }) => (
            <h1 className="text-2xl font-bold mt-6 mb-4 text-content" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-xl font-bold mt-5 mb-3 text-content" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-lg font-semibold mt-4 mb-2 text-content" {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 className="text-base font-semibold mt-3 mb-2 text-content" {...props} />
          ),
          // Lists
          ul: ({ node, ...props }) => (
            <ul className="list-disc list-inside my-2 space-y-1" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal list-inside my-2 space-y-1" {...props} />
          ),
          // Paragraphs
          p: ({ node, ...props }) => (
            <p className="my-2 leading-relaxed" {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownViewer;
